import {
  Button,
  Container,
  Form,
  Header,
  SpaceBetween,
  Spinner,
} from "@cloudscape-design/components-themed";
import { ClickDetail } from "@cloudscape-design/components-themed/internal/events";
import {
  ChangeEvent,
  createRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAppDispatch } from "../../app/hook";
import {
  usePostAnsweredApplicationUploadMutation,
  usePostAnsweredApplicationRegistrationMutation,
} from "../../features/api";
import { Messages } from "../../features/error/messages";
import { addNotification } from "../../features/error/notifications-slice";
import { getExt } from "../../utils/common";
import { downloadBase64File } from "../../utils/downLoad";
import { formatLabel } from "../../utils/format";
import { FileUpload } from "../UI/file-upload/FileUpload";
import { showModal } from "../UI/modal/modal-slice";
import ErrorTable, {
  MessageItem,
  getMessageItemList,
} from "../UI/table/ErrorTable";
import { COMMON_LABELS, COMMON_CONFIGS } from "../default-constants";
import { CONFIGS, LABELS } from "./constants";
import { AnsweredApplicationRegistration } from "../../features/api-interfaces";

/**
 * 加盟店回答状況登録画面
 */
export const Main = () => {
  const dispatch = useAppDispatch();
  const inputRef = useRef<HTMLInputElement>(null);
  const errorRef = createRef<HTMLDivElement>();
  const [uploadAnsweredApplicationFile] =
    usePostAnsweredApplicationUploadMutation();
  const [registerAnsweredApplication] =
    usePostAnsweredApplicationRegistrationMutation();
  const [uploadFile, setUploadFile] = useState<File | undefined>(undefined);
  const [errorText, setErrorText] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessageList, setErrorMessageList] = useState<MessageItem[]>([]);

  /**
   * ref.current.scrollIntoView() を呼び出してスクロール
   */
  const scrollToBottomOfList = useCallback(() => {
    errorRef?.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  }, [errorRef]);

  useEffect(() => {
    // ページが描画されたらリストの末尾までスクロール
    scrollToBottomOfList();
  }, [errorMessageList, scrollToBottomOfList]);

  /**
   * ファイルの入力チェック
   * @param {File | undefined} uploadFile
   * @returns {boolean} true: 入力エラー
   */
  const isInputError = (uploadFile: File | undefined) => {
    if (!uploadFile) {
      const messageId = "KGRERR054" as keyof Messages;
      dispatch(
        addNotification({
          msgId: messageId,
          msgArgs: [],
        })
      );
      return true;
    } else {
      return false;
    }
  };

  /**
   * 確認ボタンOK押下時の処理.
   * @param {FormData} fileData ファイル
   */
  const merchantApplication = async (fileData: FormData) => {
    // スピナー起動 & ボタン非活性
    setIsUploading(true);

    setErrorText("");

    // 加盟店回答状況アップロードAPI呼び出し
    uploadAnsweredApplicationFile(fileData)
      .unwrap()
      .then((res) => {
        setErrorMessageList([]);

        // アップロード後初期化
        setUploadFile(undefined);

        if (res.status === COMMON_CONFIGS.API.STATUS_ERROR) {
          if (res.errorMessageList?.length !== 0) {
            setErrorMessageList(getMessageItemList(res.errorMessageList));
          }
          if (res.item) {
            downloadBase64File(res.item);
          }
          // エラーでない場合は登録APIを呼び出し
        } else if (res.applicationStatusNos) {
          dispatch(
            showModal({
              title: COMMON_LABELS.TITLES.DL,
              content: formatLabel(LABELS.REGISTRATION_COUNT, [
                res.applicationStatusNos.length,
              ]),
              negativeBtn: COMMON_LABELS.NEGATIVE_BUTTON.CANCEL,
              positiveBtn: COMMON_LABELS.POSITIVE_BUTTON.OK,
              positiveFn: () => {
                answeredApplicationRegistration(res.applicationStatusNos);
              },
            })
          );
        }
      })
      .finally(() => {
        // スピナー停止 & ボタン活性
        setIsUploading(false);
      });
  };

  /**
   * 加盟店回答状況登録処理
   * @param applicationStatusNos 更新対象申請ステータス番号
   */
  const answeredApplicationRegistration = async (
    applicationStatusNos: number[]
  ) => {
    // スピナー起動 & ボタン非活性
    setIsUploading(true);

    setErrorText("");

    const registrationRequest = {
      applicationStatusNos: applicationStatusNos,
    } as AnsweredApplicationRegistration;

    await registerAnsweredApplication(registrationRequest);
    // スピナー停止 & ボタン活性
    setIsUploading(false);
  };
  /**
   * アップロードボタン押下処理
   */
  const onClickButton = async () => {
    if (isInputError(uploadFile)) {
      return;
    }

    const fileData = new FormData();
    fileData.append(CONFIGS.FILE, uploadFile!);

    dispatch(
      showModal({
        title: COMMON_LABELS.TITLES.DL,
        content:
          uploadFile !== undefined
            ? formatLabel(
                `${COMMON_LABELS.CONTENTS.APPLICATION_CONFIRM} ${LABELS.APPLICATION_CONFIRM}`,
                [uploadFile.name]
              )
            : "",
        negativeBtn: COMMON_LABELS.NEGATIVE_BUTTON.CANCEL,
        positiveBtn: COMMON_LABELS.POSITIVE_BUTTON.OK,
        positiveFn: () => {
          merchantApplication(fileData);
        },
      })
    );

    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  /**
   * 選択したファイルを設定する.
   * @param {ChangeEvent<HTMLInputElement>} event
   */
  const onChangeFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if (!files?.[0]) {
      return;
    }

    if (CONFIGS.FILE_EXTENSION !== getExt(files[0].name.toLowerCase())) {
      dispatch(
        addNotification({
          msgId: "KGRERR057",
          msgArgs: [],
        })
      );
      return;
    }

    setUploadFile(files[0]);
    setErrorText(LABELS.ERROR_TEXT_LABEL);
  };

  /**
   * 参照ボタンイベント
   * @param {CustomEvent<ClickDetail>} event
   */
  const onUpload = (event: CustomEvent<ClickDetail>) => {
    event.preventDefault();
    inputRef.current?.click();
  };

  return (
    <Container
      header={
        <Header variant={"h2"} description={""}>
          {LABELS.APPLICATION_FILE}
        </Header>
      }
    >
      <SpaceBetween direction={"vertical"} size={"xxl"}>
        <Form
          actions={
            <SpaceBetween direction={"horizontal"} size={"l"}>
              {isUploading && <Spinner size={"big"} />}
              <Button
                iconAlign={"right"}
                onClick={onClickButton}
                variant={"primary"}
                disabled={isUploading}
              >
                {COMMON_LABELS.BTNS.UL}
              </Button>
            </SpaceBetween>
          }
        >
          <FileUpload
            inputRef={inputRef}
            label={""}
            name={"applicationFile"}
            placeholder={LABELS.FILE_PLACEHOLDER}
            accept={CONFIGS.FILE_EXTENSION}
            fileName={uploadFile ? uploadFile.name : ""}
            setUploadFile={setUploadFile}
            errorText={errorText}
            setErrorText={setErrorText}
            onChange={onChangeFile}
            onUpload={onUpload}
          />
        </Form>
        {errorMessageList.length !== 0 && (
          <SpaceBetween direction={"vertical"} size={"xxs"}>
            <ErrorTable data={errorMessageList} title={LABELS.ERROR_TITLE} />
            <div id={"error"} ref={errorRef} />
          </SpaceBetween>
        )}
      </SpaceBetween>
    </Container>
  );
};
